import { GpsType } from '../../../../../../../global/components/layout/layout-models';
import { getSummaryPlan, postLayDcPlan, validateLayDcPlan } from '../../../../../../../global/ducks/benadmin-api';
import { getFormattedDate } from '../../../../../../../global/helpers/date-helpers';
import { getCurrencyFormat } from '../../../../../../../global/helpers/format-helpers';
import { YesNoIndicators } from '../../../../../../../global/models/api-model';
import { ArcRoutes } from '../../../../../constants/routes';
import { DocuSignRequest } from '../../../../docusign/models/docusign-models';
import { dispatchSetPlanSummary } from '../../../institution-slice';
import { PensionPlanTypes } from '../../../plan-history/plan-history-models';
import { ContributionAmountType, IContributionOverride, LayDcPlanAdoptionContentModel, LayDcPlanFormModel, ParticipantModel, PlanSubmitRequest, QccoStatus } from '../../models/plan-adoption-form-models';
import { IPlanSubmitRequest } from '../../models/plan-adoption-requests';
import { PlanAdoptionRoutes } from '../../routes';
import { LayDcPlanAdoptionWizard } from './lay-dc-plan-adoption-wizard';

export class LayDcReviewWizard extends LayDcPlanAdoptionWizard {

	getFormContentModel(): LayDcPlanAdoptionContentModel {

		const institutionSettings = this.epiContent?.institutionSettingsState?.epiContent;
		const content = super.getFormContentModel();
		const requiresIndemnification = this.isIndemnificationRequired();

		return {
			...content,
			gpsType: GpsType.LayDcAdoptionReview,
			pageTitle: institutionSettings?.layDcReviewTitle,
			PageDescription: institutionSettings?.adoptLayDCReviewDescription,
			adoptDetailsTitle: institutionSettings?.adoptLayDCDetailsTitle,
			submitLabel: institutionSettings?.adoptLayRSVPReviewSubmitLabel,
			cancelLabel: institutionSettings?.adoptLayRSVPReviewCancelLabel,
			docuSignSubject: institutionSettings?.adoptLayDCDocuSignSubject,
			docuSignFinishText: institutionSettings?.adoptLayDCDocusignFinishText,
			docuSignTemplateId: requiresIndemnification ? institutionSettings?.adoptIndemnifyLayDCDocuSignTemplateId : institutionSettings?.adoptLayDCDocuSignTemplateId,
		};
	}

	getPreviousStepUrl() {
		return `${this.wizardOptions?.featureRootPath}${PlanAdoptionRoutes.AdoptionDetails}`;
	}

	getNextStepUrl() {
		return `${this.wizardOptions?.institutionRootPath}${ArcRoutes.PlanSummary}`;
	}

	onSuccessQueryParams() {
		return `?formType=${this.wizardOptions?.formType}&form=${PensionPlanTypes.LayDc}`;
	}

	onSuccess(request: PlanSubmitRequest) {
		this.dispatch(getSummaryPlan(request.clientId, dispatchSetPlanSummary(this.dispatch)));
		super.onSuccess(request);
	}

	onSubmit(request: PlanSubmitRequest) {
		const payload = this.createSubmitModel(request?.values as LayDcPlanFormModel);
		this.dispatch(postLayDcPlan(request?.clientId, payload, () => this.onSuccess(request)));
	}

	createSubmitModel(formValue: LayDcPlanFormModel): IPlanSubmitRequest {

		const contributionOverride: IContributionOverride[] = formValue?.participants?.map((participant) => this.mapParticipantToContributionOverride(participant));
		const filledContributionOverride = contributionOverride?.filter((override) => override?.employee_name || override?.contribution_type);

		const request: IPlanSubmitRequest = {
			dc_plan: {
				plan_effective_date: formValue?.adoption_date,
				latest_amendment_attributes: {
					employer_base: Number(formValue?.employer_base_contribution_percentage)?.toFixed(0),
					employer_match: Number(formValue?.employer_match_contribution_percentage)?.toFixed(0),
					min_scheduled_hours: formValue?.minimum_scheduleds_hours?.toFixed(0),
					employer_override_allowed: formValue?.allow_multiple_contributions ? 'Y' : 'N',
				},
				docusign_envelope_id: formValue.envelopeId,
			},
			qcco_status: {
				supported_by_church: formValue?.qccoQuestion1 === YesNoIndicators.Yes ? 'Y' : 'N',
				offers_services_to_public: formValue?.qccoQuestion2 === YesNoIndicators.Yes ? 'Y' : 'N',
				receives_gov_support: formValue?.qccoQuestion3 === YesNoIndicators.Yes ? 'Y' : 'N',
			},
		};

		if (filledContributionOverride?.length > 0) {
			request.dc_plan.contribution_overrides = filledContributionOverride;
		}

		return request;
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	onValidateRequest(request: PlanSubmitRequest, onValidationSucess?: (request: PlanSubmitRequest) => void, onFailure?: () => any) {
		const payload = this.createSubmitModel(request?.values as LayDcPlanFormModel);

		this.dispatch(validateLayDcPlan(
			request?.clientId,
			payload,
			() => onValidationSucess(request), onFailure),
		);
	}

	prepareDocuSignRequest(): DocuSignRequest {

		const defaultRequest = super.prepareDocuSignRequest();
		const initialValues = this.getFormInitialValue();
		const memberTypeOptions = this.getMemberTypeOptions();
		const employerContributionStatusOptions = this.getEmployerContributionStatusOptions();
		const labelSettings = this.epiContent?.labelSettingState?.epiContent;
		const content = this.getFormContentModel();
		const memberValue = memberTypeOptions?.find((option) => option.value === initialValues?.member_type);
		const employerContributionStatusValue = employerContributionStatusOptions?.find((option) => option.value === initialValues?.employer_contribution_status);
		const institution = this.wizardOptions?.currentInstitution;
		const institutionAddress1 = `${institution?.physical_address_1}${institution?.physical_address_2}${institution?.physical_address_3}`;
		const institutionAddress2 = `${institution?.physical_city}, ${institution?.physical_state} ${institution?.physical_postal_code}`;

		const customTextTabs = [
			{
				tabLabel: 'indemnified',
				value: initialValues?.indemnificationEnvelopeId ? content?.indemnifySuccessLabel : content?.indemnifySkippedLabel,
			},
			{
				tabLabel: 'qcco_qualified',
				value: this.calculateQccoStatus(initialValues) === QccoStatus.Qualified ? labelSettings?.yesText : labelSettings?.noText,
			},
			{
				tabLabel: 'complyEpiscopalChurchLayPensionSystem',
				value: initialValues?.complyEpiscopalChurchLayPensionSystem === YesNoIndicators.Yes ? labelSettings?.yesText : labelSettings?.noText,
			},
			{
				tabLabel: 'organization_name',
				value: institution?.organization_name,
			},
			{
				tabLabel: 'organization_address1',
				value: institutionAddress1,
			},
			{
				tabLabel: 'organization_address2',
				value: institutionAddress2,
			},
			{
				tabLabel: 'client_id',
				value: institution?.org_client_nbr,
			},
			{
				tabLabel: 'adoption_date',
				value: getFormattedDate(initialValues?.adoption_date),
			},
			{
				tabLabel: 'eligible_population',
				value: memberValue?.label,
			},
			{
				tabLabel: 'supported_by_church',
				value: initialValues?.qccoQuestion1 === YesNoIndicators.Yes ? labelSettings?.yesText : labelSettings?.noText,
			},
			{
				tabLabel: 'offers_services_to_public',
				value: initialValues?.qccoQuestion2 === YesNoIndicators.Yes ? labelSettings?.yesText : labelSettings?.noText,
			},
			{
				tabLabel: 'receives_gov_support',
				value: initialValues?.qccoQuestion3 === YesNoIndicators.Yes ? labelSettings?.yesText : labelSettings?.noText,
			},
			{
				tabLabel: 'contribution_type',
				value: employerContributionStatusValue?.label,
			},
			{
				tabLabel: 'employer_base',
				value: initialValues?.employer_base_contribution_amount_type === ContributionAmountType.Dollar ? getCurrencyFormat(initialValues?.employer_base_contribution_amount) : `${initialValues?.employer_base_contribution_percentage}%`,
			},
			{
				tabLabel: 'employer_match',
				value: initialValues?.employer_match_contribution_amount_type === ContributionAmountType.Dollar ? getCurrencyFormat(initialValues?.employer_match_contribution_amount) : `${initialValues?.employer_match_contribution_percentage}%`,
			},
			{
				tabLabel: 'scheduled_hour',
				value: initialValues.minimum_scheduleds_hours?.toString(),
			},
		];

		const participants = initialValues?.participants?.filter((participant) => participant?.participant_name || participant?.contribution_type);

		if (participants?.length > 0) {
			participants.forEach((participant, index) => this.pushTextTabForParticipant(customTextTabs, index, participant));
		}

		const docuSignRequest: DocuSignRequest = {
			...defaultRequest,
			tabs: {
				textTabs: customTextTabs,
			},
		};

		return docuSignRequest;
	}

	// eslint-disable-next-line class-methods-use-this
	pushTextTabForParticipant(customTextTabs: { tabLabel: string, value: string }[], index: number, participant: ParticipantModel) {
		if (!customTextTabs || (!participant?.participant_name && !participant?.contribution_type)) return;

		// The participants fields index is 1-based
		const tabIndex = index + 1;

		customTextTabs.push({
			tabLabel: `participant_${tabIndex}_name`,
			value: participant?.participant_name,
		});

		customTextTabs.push({
			tabLabel: `participant_${tabIndex}_contribution_type`,
			value: participant?.contribution_type,
		});

		const contributionValueAmount = participant?.contribution_value_type === ContributionAmountType.Dollar ? getCurrencyFormat(participant?.contribution_amount) : `${participant?.contribution_percentage}%`;
		const contributionOther = participant?.contribution_other ?? '';

		customTextTabs.push({
			tabLabel: `participant_${tabIndex}_contribution_value`,
			value: `${contributionValueAmount}${contributionValueAmount?.length > 0 ? ` ${contributionOther}` : contributionOther}`,
		});
	}
}